/**
 * Auth API Service (Microservicio de Autenticación y Cuentas)
 * Conecta con los endpoints del backend y en caso de que aún no existan o fallen,
 * simula la llamada cargando el mock (.json) desde /mocks/auth/
 */
import { User, LeagueSummary } from '../models/types';
import { STORAGE_PREFIX } from './http-client';
import { apiSimulator, refreshAuthToken } from './api-simulator.service';
import { getMockJson } from './mock-registry';

export const authApi = {
  /**
   * POST /api/auth/login
   * Real backend call: POST /api/auth/login
   * Mock fallback: /mocks/auth/login.json
   */
  async login(credentials: {
    email: string;
    password?: string;
  }): Promise<{ user: User; token: string; leagues: LeagueSummary[] }> {
    const loginPayload = {
      user: credentials.email,
      password: credentials.password || '',
    };

    const fallbackName = credentials.email.includes('@')
      ? credentials.email.split('@')[0]
      : credentials.email;

    // POST /api/auth/login no devuelve datos (HTTP 204 No Content en la API real, {} en el mock)
    await apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: '/api/auth/login',
      body: loginPayload,
      service: 'auth',
      mockName: 'login',
    });

    // La identidad del usuario se obtiene del endpoint /api/users/me con la cookie de sesión establecida
    const meRes = await this.getMe();
    if (!meRes?.user || !meRes.user.id || !meRes.user.email) {
      throw new Error('No se ha podido recuperar la identidad del usuario');
    }

    const user: User = meRes.user;
    const token = meRes.token || 'cookie-session';
    const leagues: LeagueSummary[] = [];

    // Guardar token y usuario en localStorage para la sesión
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(`${STORAGE_PREFIX}token`, token);
      localStorage.setItem(`${STORAGE_PREFIX}user`, JSON.stringify(user));
    }

    return { user, token, leagues };
  },

  /**
   * POST /api/auth/register
   * Email registration: 202 Accepted (empty body)
   */
  async register(payload: { email: string; password: string }): Promise<void> {
    await apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: '/api/auth/register',
      body: {
        email: payload.email,
        password: payload.password,
      },
      service: 'auth',
      mockName: 'login',
      allowMockFallback: false,
    });
  },

  /**
   * POST /api/auth/registration/resend
   * Resend confirmation email: 202 Accepted (empty body)
   */
  async resendRegistration(payload: { email: string }): Promise<void> {
    await apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: '/api/auth/registration/resend',
      body: {
        email: payload.email,
      },
      service: 'auth',
      mockName: 'login',
      allowMockFallback: false,
    });
  },

  /**
   * POST /api/auth/registration/verify
   * Verify confirmation token: 204 No Content
   */
  async verifyRegistration(payload: { token: string }): Promise<void> {
    await apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: '/api/auth/registration/verify',
      body: {
        token: payload.token,
      },
      service: 'auth',
      mockName: 'login',
      allowMockFallback: false,
    });
  },

  /**
   * POST /api/auth/refresh
   * Real endpoint: POST /api/auth/refresh
   * Invoca el refresco del token de autenticación utilizando las cookies HttpOnly
   */
  async refresh(): Promise<boolean> {
    return refreshAuthToken();
  },

  /**
   * POST /api/auth/logout
   * Real endpoint: POST /api/auth/logout
   * Mock fallback: /mocks/auth/logout.json
   */
  async logout(): Promise<{ success: boolean }> {
    let res = { success: true };
    try {
      res = await apiSimulator.callWithMockFallback<{ success: boolean }>({
        method: 'POST',
        endpoint: '/api/auth/logout',
        body: null,
        service: 'auth',
        mockName: 'logout',
      });
    } catch (e) {
      console.warn('[authApi] Error de red o respuesta en /api/auth/logout:', e);
    }

    if (typeof localStorage !== 'undefined') {
      const theme = localStorage.getItem('fm_theme');
      const lang = localStorage.getItem('fm_language');
      localStorage.clear();
      if (theme) localStorage.setItem('fm_theme', theme);
      if (lang) localStorage.setItem('fm_language', lang);
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }

    return res;
  },

  /**
   * GET /api/users/me
   * Real endpoint: GET /api/users/me
   * Mock fallback: /mocks/auth/users-me.json
   */
  async getMe(): Promise<{ user: User | null; token: string | null }> {
    let token: string | null = null;
    if (typeof localStorage !== 'undefined') {
      token = localStorage.getItem(`${STORAGE_PREFIX}token`);
    }

    const meData = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: '/api/users/me',
      service: 'auth',
      mockName: 'users-me',
    });

    let storedUser: User | null = null;
    if (typeof localStorage !== 'undefined') {
      const userRaw = localStorage.getItem(`${STORAGE_PREFIX}user`);
      if (userRaw) {
        try {
          storedUser = JSON.parse(userRaw);
        } catch {
          // ignore
        }
      }
    }

    const user: User = {
      id: meData?.id || storedUser?.id || 'usr-01',
      name: meData?.username || storedUser?.name || 'Mánager General',
      email: meData?.email || storedUser?.email || 'manager@lom.es',
      role: meData?.roles?.[0] || storedUser?.role || 'Director Técnico & Club Manager',
      avatar: meData?.avatar || storedUser?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
    };

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(`${STORAGE_PREFIX}user`, JSON.stringify(user));
    }

    return { user, token };
  },
};
